import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
} from "@porchpass/ui";
import { addCondition, reactivateOptionalCondition } from "src/api/preApproval";
import type {
  PreApprovalCondition,
  ConditionSection,
} from "src/api/preApproval/types";
import { OptionalConditionPicker } from "./OptionalConditionPicker";

type AddMode = "optional" | "create";

interface AddConditionFormProps {
  section: ConditionSection;
  onCancel: () => void;
  onSuccess: () => void;
}

export function AddConditionForm({
  section,
  onCancel,
  onSuccess,
}: AddConditionFormProps) {
  const { applicationId } = useParams<{ applicationId: string }>();
  const [mode, setMode] = useState<AddMode>("optional");
  const [selectedCondition, setSelectedCondition] =
    useState<PreApprovalCondition | null>(null);
  const [title, setTitle] = useState("");
  const [internalDescription, setInternalDescription] = useState("");
  const [externalDescription, setExternalDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!applicationId) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      if (mode === "optional") {
        if (!selectedCondition) {
          setError("Select an optional condition to add.");
          return;
        }
        await reactivateOptionalCondition(
          applicationId,
          selectedCondition,
          section,
        );
      } else {
        const trimmedTitle = title.trim();
        if (!trimmedTitle) {
          setError("Enter a condition title.");
          return;
        }
        await addCondition(applicationId, section, {
          title: trimmedTitle,
          internal_description: internalDescription.trim() || undefined,
          external_description: externalDescription.trim() || undefined,
        });
      }
      onSuccess();
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Unable to add condition.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add condition</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <form
            id="add-condition-form"
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
            className="flex flex-col gap-4"
          >
            <div className="flex gap-2">
              <Button
                type="button"
                size="s"
                variant={mode === "optional" ? "default" : "secondary"}
                onClick={() => {
                  setMode("optional");
                  setError(null);
                }}
              >
                From optional list
              </Button>
              <Button
                type="button"
                size="s"
                variant={mode === "create" ? "default" : "secondary"}
                onClick={() => {
                  setMode("create");
                  setError(null);
                }}
              >
                Create new
              </Button>
            </div>

            {mode === "optional" ? (
              <OptionalConditionPicker
                selectedId={selectedCondition?.id ?? null}
                onSelect={setSelectedCondition}
              />
            ) : (
              <>
                <Input
                  label="Title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                  clearable={false}
                />
                <Textarea
                  label="Internal description"
                  value={internalDescription}
                  onChange={(event) =>
                    setInternalDescription(event.target.value)
                  }
                  rows={3}
                  showOptional
                />
                <Textarea
                  label="External description"
                  value={externalDescription}
                  onChange={(event) =>
                    setExternalDescription(event.target.value)
                  }
                  rows={3}
                  showOptional
                />
              </>
            )}

            {error && <p className="text-sm text-[#B91C1C]">{error}</p>}
          </form>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-condition-form"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add condition"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
